import React, { useState, useEffect } from 'react';
import { Heart, Users, Home, Search } from 'lucide-react';
import apiClient from "../../api/client";


/**
 * Relationship Discovery Filters Component
 * 
 * Allows users to filter discovery/matching based on:
 * - Preferred relationship styles (mono/poly/polygamous/etc.)
 * - Preferred relationship goals (triads, quads, families, etc.)
 * - Preferred household arrangements
 * 
 * This makes Unity Love's matching poly-aware.
 */
export function RelationshipDiscoveryFilters({ token, onSave }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Available options from backend
  const [options, setOptions] = useState({
    relationship_styles: [],
    relationship_goals: [],
    household_preferences: [],
  });
  
  // User's current preferences (what they want to see)
  const [preferredStyles, setPreferredStyles] = useState([]);
  const [preferredGoals, setPreferredGoals] = useState([]);
  const [preferredHouseholds, setPreferredHouseholds] = useState([]);
  
  // Load options and current preferences
  useEffect(() => {
    async function loadData() {
      try {
        // Load dropdown options
        const optionsRes = await apiClient.get(`/relationship/options`);
        setOptions(optionsRes.data);
        
        // Load user's current discovery preferences
        const prefsRes = await apiClient.get(`/relationship/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const prefs = prefsRes.data;
        setPreferredStyles(prefs.preferred_relationship_styles || []);
        setPreferredGoals(prefs.preferred_relationship_goals || []);
        setPreferredHouseholds(prefs.preferred_household_preferences || []);
      } catch (err) {
        console.error('Failed to load relationship preferences:', err);
        setError('Failed to load preferences');
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, [token]);
  
  const toggleFromArray = (value, setter) => {
    setter(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };
  
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      await apiClient.put(
        `/relationship/preferences`,
        {
          preferred_relationship_styles: preferredStyles.length > 0 ? preferredStyles : null,
          preferred_relationship_goals: preferredGoals.length > 0 ? preferredGoals : null,
          preferred_household_preferences: preferredHouseholds.length > 0 ? preferredHouseholds : null,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (onSave) onSave();
    } catch (err) {
      console.error('Failed to save relationship preferences:', err);
      setError(err.response?.data?.detail || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="rounded-lg bg-gray-900/60 border border-gray-700 p-6">
        <div className="text-gray-400 text-center">Loading relationship filters...</div>
      </div>
    );
  }
  
  return (
    <div className="rounded-lg bg-gray-900/60 border border-gray-700 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Heart className="w-5 h-5 text-rose-400 fill-current" />
            Poly & Relationship Discovery Filters
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Tune who you see based on relationship style, goals, and household fit
          </p>
        </div>
      </div>
      
      {error && (
        <div className="rounded-lg bg-red-900/30 border border-red-500 px-4 py-3 text-red-200 text-sm">
          {error}
        </div>
      )}
      
      {/* Preferred Relationship Styles */}
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
          <Users className="w-4 h-4" />
          Show me people who practice:
        </label>
        <div className="flex flex-wrap gap-2">
          {options.relationship_styles.map(style => (
            <button
              key={style.value}
              type="button"
              onClick={() => toggleFromArray(style.value, setPreferredStyles)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                preferredStyles.includes(style.value)
                  ? 'bg-rose-600 text-white border-rose-500'
                  : 'bg-gray-800 text-gray-300 border-gray-600 hover:border-gray-500'
              }`}
            >
              {style.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          {preferredStyles.length === 0 
            ? 'No filter - show all styles' 
            : `Filtering for ${preferredStyles.length} style${preferredStyles.length !== 1 ? 's' : ''}`
          }
        </p>
      </div>
      
      {/* Preferred Relationship Goals */}
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
          <Search className="w-4 h-4" />
          I'm open to connections like:
        </label>
        <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto bg-gray-800/50 rounded-lg p-3 border border-gray-700">
          {options.relationship_goals.map(goal => (
            <button
              key={goal.value}
              type="button"
              onClick={() => toggleFromArray(goal.value, setPreferredGoals)}
              className={`text-left text-xs px-3 py-2 rounded-lg transition-all ${
                preferredGoals.includes(goal.value)
                  ? 'bg-rose-600 text-white border border-rose-500'
                  : 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-gray-500'
              }`}
            >
              {goal.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          {preferredGoals.length === 0
            ? 'No filter - show all goals'
            : `Filtering for ${preferredGoals.length} goal${preferredGoals.length !== 1 ? 's' : ''}`
          }
        </p>
      </div>
      
      {/* Preferred Household Arrangements */}
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
          <Home className="w-4 h-4" />
          Household & living preferences:
        </label>
        <div className="flex flex-wrap gap-2">
          {options.household_preferences.map(pref => (
            <button
              key={pref.value}
              type="button"
              onClick={() => toggleFromArray(pref.value, setPreferredHouseholds)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                preferredHouseholds.includes(pref.value)
                  ? 'bg-rose-600 text-white border-rose-500'
                  : 'bg-gray-800 text-gray-300 border-gray-600 hover:border-gray-500'
              }`}
            >
              {pref.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          {preferredHouseholds.length === 0
            ? 'No filter - show all arrangements'
            : `Filtering for ${preferredHouseholds.length} preference${preferredHouseholds.length !== 1 ? 's' : ''}`
          }
        </p>
      </div>
      
      {/* Info Box */}
      <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
        <p className="text-xs text-blue-200">
          💡 <strong>Pro tip:</strong> Leave filters empty to see everyone. Select specific options to narrow 
          your discovery to people whose relationship style aligns with yours.
        </p>
      </div>
      
      {/* Save Button */}
      <div className="pt-4 border-t border-gray-700">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-rose-600 to-pink-600 px-6 py-3 text-sm font-semibold text-white hover:from-rose-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-rose-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {saving ? 'Saving Filters...' : 'Save Discovery Filters'}
        </button>
      </div>
    </div>
  );
}
